import React, { useMemo, useState } from "react";
import { Info, SlidersHorizontal, X } from "lucide-react";
import { cn } from "../../libs/utils";
import { Button } from "./button";
import { Input } from "./input";
import PaginatedSelect from "./PaginatedSelect";
import { localDateTimeToUtc, toLocalDateTimeInput } from "../../services/dateTime";

const ONE_MONTH_MS = 31 * 24 * 60 * 60 * 1000;

const hasFilterValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const getFieldActiveCount = (field, values) => {
  if (field.type === "datetime-range") {
    return hasFilterValue(values[field.fromName]) || hasFilterValue(values[field.toName])
      ? 1
      : 0;
  }

  return hasFilterValue(values[field.name]) ? 1 : 0;
};

const getRangeKey = (field) => `${field.fromName}:${field.toName}`;

const getRangeError = (fromValue, toValue) => {
  if (!fromValue || !toValue) return "";

  const fromTime = new Date(fromValue).getTime();
  const toTime = new Date(toValue).getTime();

  if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) return "";
  if (toTime < fromTime) return "End date must be after start date.";
  if (toTime - fromTime > ONE_MONTH_MS) {
    return "Date range cannot be more than one month.";
  }

  return "";
};

const AdvancedFilters = ({
  fields = [],
  values = {},
  onChange,
  onClear,
  className,
  requirePlant = false,
  hasPlant = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rangeErrors, setRangeErrors] = useState({});
  const needsPlant = requirePlant && !hasPlant;
  const activeCount = useMemo(
    () =>
      fields.reduce(
        (count, field) => count + getFieldActiveCount(field, values),
        0
      ),
    [fields, values]
  );

  if (!fields.length) return null;

  const handleRangeChange = (field, fieldName, nextValue) => {
    const utcValue = localDateTimeToUtc(nextValue);
    const nextFrom =
      fieldName === field.fromName ? utcValue : values[field.fromName] || "";
    const nextTo =
      fieldName === field.toName ? utcValue : values[field.toName] || "";
    const error = getRangeError(nextFrom, nextTo);
    const rangeKey = getRangeKey(field);

    if (fieldName === field.fromName && error) {
      setRangeErrors((current) => ({
        ...current,
        [rangeKey]: "",
      }));
      onChange(field.fromName, utcValue);
      onChange(field.toName, "");
      return;
    }

    setRangeErrors((current) => ({
      ...current,
      [rangeKey]: error,
    }));

    if (error) return;

    onChange(fieldName, utcValue);
  };

  const handleClear = () => {
    setRangeErrors({});
    onClear();
  };

  const renderField = (field) => {
    const value = values[field.name] ?? "";
    const isDisabled = Boolean(field.disabled || needsPlant);

    if (field.type === "datetime-range") {
      const fromValue = toLocalDateTimeInput(values[field.fromName]);
      const toValue = toLocalDateTimeInput(values[field.toName]);
      const rangeError = rangeErrors[getRangeKey(field)] || "";

      return (
        <div className="grid gap-2">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="grid gap-1">
              <span className="text-[11px] font-medium uppercase text-slate-400">
                From
              </span>
              <Input
                id={`filter_${field.fromName}`}
                aria-label={`${field.label} From`}
                type="datetime-local"
                value={fromValue}
                max={toValue || undefined}
                disabled={isDisabled}
                onChange={(event) =>
                  handleRangeChange(field, field.fromName, event.target.value)
                }
              />
            </div>
            <div className="grid gap-1">
              <span className="text-[11px] font-medium uppercase text-slate-400">
                To
              </span>
              <Input
                id={`filter_${field.toName}`}
                aria-label={`${field.label} To`}
                type="datetime-local"
                value={toValue}
                min={fromValue || undefined}
                disabled={isDisabled}
                onChange={(event) =>
                  handleRangeChange(field, field.toName, event.target.value)
                }
              />
            </div>
          </div>
          {rangeError && (
            <p className="text-xs font-medium text-red-600">{rangeError}</p>
          )}
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <PaginatedSelect
          value={value}
          onChange={(nextValue) => onChange(field.name, nextValue)}
          options={field.options || []}
          disabled={isDisabled}
          placeholder={field.placeholder || `All ${field.label}`}
        />
      );
    }

    return (
      <Input
        id={`filter_${field.name}`}
        type={field.type || "text"}
        value={value}
        min={field.min}
        step={field.step}
        disabled={isDisabled}
        onChange={(event) => onChange(field.name, event.target.value)}
        placeholder={field.placeholder || field.label}
      />
    );
  };

  return (
    <div className={cn("relative w-full sm:w-auto", className)}>
      <Button
        type="button"
        variant="outline"
        className="w-full gap-2 sm:w-auto"
        onClick={() => setIsOpen((current) => !current)}
      >
        <SlidersHorizontal className="h-4 w-4 shrink-0" />
        <span>Filters</span>
        {activeCount > 0 && (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white">
            {activeCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-30 w-[calc(100vw-2rem)] max-w-3xl rounded-lg border border-slate-100 bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,0.14)] sm:w-[640px]">
          <div className="mb-3 flex items-center justify-between gap-3 px-1">
            <p className="text-sm font-semibold text-slate-900">Filters</p>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  className="gap-1"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                Done
              </Button>
            </div>
          </div>

          {needsPlant && (
            <div className="mb-3 flex gap-2 rounded-md bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p>Select a plant before using custom filters.</p>
            </div>
          )}

          <div className="grid max-h-[min(62vh,520px)] grid-cols-1 gap-3 overflow-y-auto px-1 pb-4 pt-1 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map((field) => (
              <label
                key={field.name || `${field.fromName}_${field.toName}`}
                className={cn(
                  "grid gap-1.5",
                  field.type === "datetime-range" && "sm:col-span-2 lg:col-span-3"
                )}
              >
                <span className="flex items-center gap-1 text-xs font-medium text-slate-600">
                  <span>{field.label}</span>
                  {field.type === "datetime-range" && (
                    <Info
                      className="h-3.5 w-3.5 text-slate-400"
                      aria-label="Date range can include time and cannot exceed one month."
                      title="Date range can include time and cannot exceed one month."
                    />
                  )}
                </span>
                {renderField(field)}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters;
